import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import type { HouseholdArtifactsPort } from '../application/contracts';

const LEGACY_FAVORITES_KEY = 'inventory-entry-favorites-v1';
const FAVORITES_PREFIX = 'inventory-entry-favorites-v2:';
const EXPORT_ROOT = 'frozen-save-households';
const SAFE_HOUSEHOLD_ID = /^[a-z0-9][a-z0-9_-]{0,127}$/i;

const assertHouseholdId = (householdId: string): void => {
  if (!SAFE_HOUSEHOLD_ID.test(householdId)) {
    throw new Error('Identifiant local du foyer invalide.');
  }
};

export const householdFavoritesKey = (householdId: string): string => {
  assertHouseholdId(householdId);
  return `${FAVORITES_PREFIX}${householdId}`;
};

export const householdExportDirectory = (householdId: string): string => {
  assertHouseholdId(householdId);
  return `${EXPORT_ROOT}/${householdId}`;
};

const parseFavorites = (value: string | null): string[] => {
  if (value == null) return [];
  try {
    const parsed = JSON.parse(value) as unknown;
    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === 'string').slice(0, 12)
      : [];
  } catch {
    return [];
  }
};

export const loadHouseholdFavorites = (householdId: string): string[] => {
  if (typeof localStorage === 'undefined') return [];
  const key = householdFavoritesKey(householdId);
  const scoped = localStorage.getItem(key);
  if (scoped != null) return parseFavorites(scoped);

  const legacy = localStorage.getItem(LEGACY_FAVORITES_KEY);
  const favorites = parseFavorites(legacy);
  if (legacy != null) {
    localStorage.setItem(key, JSON.stringify(favorites));
    localStorage.removeItem(LEGACY_FAVORITES_KEY);
  }
  return favorites;
};

export const saveHouseholdFavorites = (
  householdId: string,
  favorites: string[],
): void => {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(
    householdFavoritesKey(householdId),
    JSON.stringify(favorites.slice(0, 12)),
  );
};

export class LocalHouseholdArtifacts implements HouseholdArtifactsPort {
  async deleteForHousehold(householdId: string): Promise<void> {
    const favoritesKey = householdFavoritesKey(householdId);
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(favoritesKey);
      localStorage.removeItem(LEGACY_FAVORITES_KEY);
    }
    if (!Capacitor.isNativePlatform()) return;
    await Filesystem.rmdir({
      path: householdExportDirectory(householdId),
      directory: Directory.Cache,
      recursive: true,
    }).catch((error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      if (/not found|not exist|does not exist|introuvable/i.test(message)) {
        return;
      }
      throw error;
    });
  }
}
